import type { Api, Session } from "../api/client.ts";
import { ApiError } from "../api/client.ts";
import { formatExpiry, planLabel } from "../plan.ts";

/**
 * Plan copy for the paywall, copied by hand from `pricing.html`'s `.tiers`
 * markup. There is no shared source between that static page and this TS
 * client, so this is a deliberate duplication — keep it in sync if the
 * marketing copy changes.
 */
interface TierCopy {
  readonly id: "free" | "day" | "pro";
  readonly name: string;
  readonly tag?: string;
  readonly price: { readonly monthly: string; readonly yearly: string };
  readonly sub: { readonly monthly: string; readonly yearly: string };
  readonly bullets: readonly string[];
  /** Index of a bullet to bold, matching the `<b>` in `pricing.html`'s Pro tier. */
  readonly emphasize?: number;
}

const TIERS: readonly TierCopy[] = [
  {
    id: "free",
    name: "Free",
    price: { monthly: "€0", yearly: "€0" },
    sub: { monthly: "One nest, for testing", yearly: "One nest, for testing" },
    bullets: [
      "One nest, up to 100 parts",
      "Full preview and part detection",
      "One DXF per part, zipped",
      "Layers exported as they came",
    ],
  },
  {
    id: "day",
    name: "Day pass",
    price: { monthly: "€39 one-off", yearly: "€39 one-off" },
    sub: { monthly: "For the job that lands on a Friday", yearly: "For the job that lands on a Friday" },
    bullets: [
      "Split an individual nest",
      "No limit on nests that can be split in a day",
      "CSV and PDF report outputs included",
      "No subscription, no renewal",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    tag: "Most shops",
    price: { monthly: "€99 / month", yearly: "€999 / year" },
    sub: {
      monthly: "€1,188 a year if you pay monthly.",
      yearly: "Works out at €83.25 a month, and saves you €189 a year.",
    },
    bullets: [
      "Open and queue multiple nests",
      "De-dupe and split multiple nests in one click",
      "Email support",
      "Significant discount over day pass",
      "16% saving over monthly when paying annually",
    ],
    emphasize: 0,
  },
];

export type Period = "monthly" | "yearly";
type Method = "card" | "code";

/** A plan chosen from `openPlanPicker()`, before an account exists yet. */
export interface PlanChoice {
  readonly plan: "free" | "day" | "pro";
  readonly period: Period;
}

/**
 * Shown in place of export when the session's own entitlement says no —
 * the free tier's one nest already spent, or a Day pass/Pro that has run
 * out. Same hand-built-overlay approach as `AuthDialog`, for the same
 * reason: a native `<dialog>` fights password-manager extensions.
 *
 * There is no payment gateway behind this yet. Choosing a plan and a
 * payment method is fully interactive, but submitting either method ends in
 * a placeholder notice, not a charge. Unlock-code redemption is the seam a
 * later phase will wire up.
 */
export class PaywallDialog {
  private readonly overlay: HTMLElement;
  private readonly card: HTMLElement;
  private readonly reason: HTMLElement;
  private readonly period: HTMLElement;
  private readonly tiers: HTMLElement;
  private readonly planStep: HTMLElement;
  private readonly methodStep: HTMLElement;
  private readonly methodSummary: HTMLElement;
  private readonly methodButtons: HTMLElement;
  private readonly cardAction: HTMLElement;
  private readonly codeAction: HTMLElement;
  private readonly codeInput: HTMLInputElement;
  private readonly redeemButton: HTMLButtonElement;
  private readonly notice: HTMLElement;
  private readonly autoRenewField: HTMLElement;
  private readonly autoRenewInput: HTMLInputElement;

  private billingPeriod: Period = "monthly";
  private resolve: ((session: Session | null) => void) | null = null;
  /** Set once a code has been redeemed in this open(), so close() can hand the caller the fresh session. */
  private redeemedSession: Session | null = null;
  private previouslyFocused: HTMLElement | null = null;

  /**
   * "upgrade" is the existing session-bound flow (`open`/`openForPlan`):
   * choosing Day/Pro moves to the payment-method step, Free has no button.
   * "signup" is `openPlanPicker`: there's no session yet, so every tier —
   * Free included — resolves the picker immediately instead of stepping
   * into payment, which the caller runs separately once an account exists.
   */
  private pickerMode: "upgrade" | "signup" = "upgrade";
  private planResolve: ((choice: PlanChoice | null) => void) | null = null;

  constructor(
    private readonly api: Api,
    private readonly root: Document = document,
  ) {
    this.overlay = root.createElement("div");
    this.overlay.className = "paywall-overlay";
    this.overlay.hidden = true;
    this.overlay.innerHTML = `
      <div class="paywall" role="dialog" aria-modal="true" aria-labelledby="paywall-title">
        <button type="button" class="paywall__close" aria-label="Close">&times;</button>

        <div class="paywall-step paywall-step-plans">
          <h2 class="paywall__title" id="paywall-title">Choose a plan</h2>
          <p class="paywall__reason"></p>

          <div class="paywall-period" role="group" aria-label="Billing period">
            <button type="button" class="paywall-period__btn" data-period="monthly" aria-pressed="true">Monthly</button>
            <button type="button" class="paywall-period__btn" data-period="yearly" aria-pressed="false">
              Yearly <span class="paywall-period__save">Save 16%</span>
            </button>
          </div>

          <div class="paywall-tiers"></div>
        </div>

        <div class="paywall-step paywall-step-method" hidden>
          <button type="button" class="linkbtn paywall__back">&larr; Back to plans</button>
          <h2 class="paywall__title">Pay with</h2>
          <p class="paywall__method-summary"></p>

          <div class="paywall-methods">
            <button type="button" class="paywall-method" data-method="card" aria-pressed="false">
              <span class="paywall-method__name">Credit card</span>
              <span class="paywall-method__hint">Visa, Mastercard, Amex</span>
            </button>
            <button type="button" class="paywall-method" data-method="code" aria-pressed="false">
              <span class="paywall-method__name">Unlock code</span>
              <span class="paywall-method__hint">Have a code from your company?</span>
            </button>
          </div>

          <div class="paywall-method-action paywall-method-action--card" hidden>
            <label class="paywall__toggle paywall__auto-renew" hidden>
              <input type="checkbox" checked />
              <span>Auto-renew this subscription</span>
            </label>
            <button type="button" class="btn btn--primary paywall__pay">Continue to payment</button>
          </div>
          <div class="paywall-method-action paywall-method-action--code" hidden>
            <label class="paywall__field">
              <span>Unlock code</span>
              <input type="text" autocomplete="off" spellcheck="false" placeholder="Paste the code from your email" />
            </label>
            <button type="button" class="btn btn--primary paywall__redeem">Redeem code</button>
          </div>

          <p class="paywall__notice" hidden></p>
        </div>
      </div>
    `;
    root.body.append(this.overlay);

    this.card = this.overlay.querySelector(".paywall")!;
    this.reason = this.overlay.querySelector(".paywall__reason")!;
    this.period = this.overlay.querySelector(".paywall-period")!;
    this.tiers = this.overlay.querySelector(".paywall-tiers")!;
    this.planStep = this.overlay.querySelector(".paywall-step-plans")!;
    this.methodStep = this.overlay.querySelector(".paywall-step-method")!;
    this.methodSummary = this.overlay.querySelector(".paywall__method-summary")!;
    this.methodButtons = this.overlay.querySelector(".paywall-methods")!;
    this.cardAction = this.overlay.querySelector(".paywall-method-action--card")!;
    this.codeAction = this.overlay.querySelector(".paywall-method-action--code")!;
    this.codeInput = this.overlay.querySelector(".paywall-method-action--code input")!;
    this.redeemButton = this.overlay.querySelector(".paywall__redeem")!;
    this.notice = this.overlay.querySelector(".paywall__notice")!;
    this.autoRenewField = this.overlay.querySelector(".paywall__auto-renew")!;
    this.autoRenewInput = this.overlay.querySelector(".paywall__auto-renew input")!;

    this.renderTiers();

    this.overlay.querySelector(".paywall__close")!.addEventListener("click", () => this.close());
    this.overlay.querySelector(".paywall__back")!.addEventListener("click", () => this.showPlans());
    this.period.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>(".paywall-period__btn");
      if (button) this.setPeriod(button.dataset["period"] as Period);
    });
    this.tiers.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-plan]");
      if (!button) return;
      const plan = button.dataset["plan"] as "free" | "day" | "pro";
      if (this.pickerMode === "signup") this.resolvePlan({ plan, period: this.billingPeriod });
      else this.showMethod(plan as "day" | "pro");
    });
    this.methodButtons.addEventListener("click", (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-method]");
      if (button) this.setMethod(button.dataset["method"] as Method);
    });
    this.overlay.querySelector(".paywall__pay")!.addEventListener("click", () => {
      this.showNotice("Card payments aren't available yet — check back soon.");
    });
    this.redeemButton.addEventListener("click", () => void this.redeemCode());

    // Same rule as the auth dialog: a click only counts as "dismiss" if it
    // started on the backdrop itself, not one that began on the card and
    // was dragged or released outside it.
    this.overlay.addEventListener("mousedown", (event) => {
      if (event.target === this.overlay) this.close();
    });
  }

  /**
   * Show the paywall for a blocked session.
   *
   * `reason`, if given, replaces the usual "you've used your free nest" /
   * "your plan expired" copy — for a refusal that isn't about the session
   * being blocked outright but about one feature it doesn't cover (combined,
   * deduplicated export on a Day pass or Free account, which can otherwise
   * export just fine).
   *
   * Resolves once it's dismissed: with the fresh session if a code was
   * redeemed, or null if it was closed without one — the same null-means-
   * unchanged convention AuthDialog uses.
   */
  open(session: Session | null, reason?: string): Promise<Session | null> {
    return this.openAt(session, "monthly", () => this.showPlans(), reason);
  }

  /**
   * Open straight on the payment-method step for `plan`, skipping the
   * plan-picker — for a visitor who already chose a tier (and, for Pro, a
   * billing period) on the marketing site before creating their account.
   * "Back to plans" on that step still gets them to the full picker, with
   * the same period selected, if they change their mind.
   */
  openForPlan(session: Session | null, plan: "day" | "pro", period: Period = "monthly"): Promise<Session | null> {
    return this.openAt(session, period, () => this.showMethod(plan));
  }

  private openAt(
    session: Session | null,
    period: Period,
    showStep: () => void,
    reasonOverride?: string,
  ): Promise<Session | null> {
    this.setPickerMode("upgrade");
    this.reason.textContent = reasonOverride ?? describeReason(session);
    // An override names a specific feature restriction rather than the
    // account being blocked outright, and reads better with more weight than
    // the usual dim, small-print reason line.
    this.reason.classList.toggle("paywall__reason--emphasis", reasonOverride !== undefined);
    this.redeemedSession = null;
    this.setPeriod(period);
    showStep();

    this.previouslyFocused = this.root.activeElement as HTMLElement | null;
    this.setBackgroundInert(true);
    this.overlay.hidden = false;
    this.root.addEventListener("keydown", this.handleKeydown, true);
    (this.overlay.querySelector(".paywall__close") as HTMLElement).focus();

    return new Promise((resolve) => {
      this.resolve = resolve;
    });
  }

  /**
   * Show the plan picker for someone who doesn't have an account yet — used
   * by the "Create an account" choice in `AuthDialog`, which hands control
   * here before any registration happens. Every tier, including Free, is a
   * button that resolves immediately with the chosen plan and billing
   * period; there is no session to attach a redeemed code or payment to
   * yet, so the method step (card/voucher) is not shown here — the caller
   * runs registration first, then for a paid plan calls `openForPlan()`
   * separately against the new session.
   *
   * Resolves `null` if closed without choosing, so the caller can fall back
   * to its own sign-in/create-account choice.
   */
  openPlanPicker(): Promise<PlanChoice | null> {
    this.setPickerMode("signup");
    this.reason.textContent = "Choose a plan to create your account.";
    this.setPeriod("monthly");
    this.showPlans();

    this.previouslyFocused = this.root.activeElement as HTMLElement | null;
    this.setBackgroundInert(true);
    this.overlay.hidden = false;
    this.root.addEventListener("keydown", this.handleKeydown, true);
    (this.overlay.querySelector(".paywall__close") as HTMLElement).focus();

    return new Promise((resolve) => {
      this.planResolve = resolve;
    });
  }

  private setPickerMode(mode: "upgrade" | "signup"): void {
    this.pickerMode = mode;
    const freeNote = this.tiers.querySelector<HTMLElement>('[data-tier="free"] .paywall-tier__note');
    const freeCta = this.tiers.querySelector<HTMLElement>('[data-tier="free"] [data-plan="free"]');
    if (freeNote) freeNote.hidden = mode === "signup";
    if (freeCta) freeCta.hidden = mode !== "signup";
  }

  /** Redeem whatever is in the code field against the signed-in account. */
  private async redeemCode(): Promise<void> {
    const code = this.codeInput.value.trim();
    if (code === "") {
      this.showNotice("Enter a code first.");
      return;
    }

    this.redeemButton.disabled = true;
    this.showNotice(null);
    try {
      await this.api.redeem(code);
      // Re-fetch rather than trust the redeem response alone, so the dialog
      // closes with the same shape of Session everything else in the app
      // already works with — limits included.
      const session = await this.api.session();
      this.redeemedSession = session;
      this.showNotice("Code redeemed. Your plan has been updated.");
      window.setTimeout(() => this.close(), 1200);
    } catch (error) {
      this.redeemButton.disabled = false;
      this.showNotice(error instanceof ApiError ? error.message : "Could not reach the server. Try again.");
    }
  }

  private renderTiers(): void {
    this.tiers.innerHTML = TIERS.map((tier) => this.tierCardHtml(tier)).join("");
  }

  private tierCardHtml(tier: TierCopy): string {
    const bullets = tier.bullets
      .map((bullet, index) => `<li>${index === tier.emphasize ? `<b>${bullet}</b>` : bullet}</li>`)
      .join("");
    const name = tier.tag ? `${tier.name} <span class="paywall-tier__tag">${tier.tag}</span>` : tier.name;
    // Free has no CTA in "upgrade" mode (every account already includes it)
    // but needs one in "signup" mode, where no account exists yet and every
    // tier — Free included — is a valid thing to pick. Both markups are
    // always rendered; `setPickerMode()` toggles which one shows.
    const cta =
      tier.id === "free"
        ? `<p class="paywall-tier__note">Included with every account</p>
           <button type="button" class="btn paywall-tier__cta" data-plan="free" hidden>Continue with Free</button>`
        : `<button type="button" class="btn btn--primary paywall-tier__cta" data-plan="${tier.id}">Choose ${tier.name}</button>`;

    return `
      <div class="paywall-tier" data-tier="${tier.id}" ${tier.id === "pro" ? "data-featured" : ""}>
        <div class="paywall-tier__name">${name}</div>
        <div class="paywall-tier__price" data-price-monthly="${tier.price.monthly}" data-price-yearly="${tier.price.yearly}">
          ${tier.price.monthly}
        </div>
        <p class="paywall-tier__sub" data-sub-monthly="${tier.sub.monthly}" data-sub-yearly="${tier.sub.yearly}">
          ${tier.sub.monthly}
        </p>
        <ul class="paywall-tier__list">${bullets}</ul>
        ${cta}
      </div>
    `;
  }

  private setPeriod(period: Period): void {
    this.billingPeriod = period;
    for (const button of this.period.querySelectorAll<HTMLButtonElement>(".paywall-period__btn")) {
      button.setAttribute("aria-pressed", String(button.dataset["period"] === period));
    }
    for (const price of this.tiers.querySelectorAll<HTMLElement>(".paywall-tier__price")) {
      price.textContent = price.dataset[period === "monthly" ? "priceMonthly" : "priceYearly"] ?? "";
    }
    for (const sub of this.tiers.querySelectorAll<HTMLElement>(".paywall-tier__sub")) {
      sub.textContent = sub.dataset[period === "monthly" ? "subMonthly" : "subYearly"] ?? "";
    }
  }

  private showPlans(): void {
    this.planStep.hidden = false;
    this.methodStep.hidden = true;
  }

  private showMethod(plan: "day" | "pro"): void {
    this.planStep.hidden = true;
    this.methodStep.hidden = false;
    this.showNotice(null);
    this.setMethod(null);

    const tier = TIERS.find((t) => t.id === plan)!;
    const price = tier.price[this.billingPeriod];
    this.methodSummary.textContent = `${tier.name} — ${price}`;

    // Auto-renew only makes sense for Pro's recurring monthly/yearly billing —
    // the Day pass is explicitly a one-off, no subscription to renew.
    this.autoRenewField.hidden = plan !== "pro";
    this.autoRenewInput.checked = true;
  }

  private setMethod(method: Method | null): void {
    for (const button of this.methodButtons.querySelectorAll<HTMLButtonElement>("[data-method]")) {
      button.setAttribute("aria-pressed", String(button.dataset["method"] === method));
    }
    this.cardAction.hidden = method !== "card";
    this.codeAction.hidden = method !== "code";
    this.showNotice(null);
    this.redeemButton.disabled = false;
    if (method !== "code") this.codeInput.value = "";
  }

  private showNotice(message: string | null): void {
    this.notice.textContent = message ?? "";
    this.notice.hidden = message === null;
  }

  /**
   * Escape and Tab, the two things a native `<dialog>` handles for free.
   * Bound once and attached/detached with the overlay's visibility, so it
   * never intercepts a keystroke while the dialog is closed.
   */
  private readonly handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = Array.from(
      this.card.querySelectorAll<HTMLElement>('button, input, a[href], [tabindex]:not([tabindex="-1"])'),
    ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;

    if (event.shiftKey && this.root.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && this.root.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /** Everything outside the overlay stops being reachable while it's open. */
  private setBackgroundInert(inert: boolean): void {
    for (const child of Array.from(this.root.body.children)) {
      if (child !== this.overlay) (child as HTMLElement).inert = inert;
    }
  }

  private hideOverlay(): void {
    this.overlay.hidden = true;
    this.setBackgroundInert(false);
    this.root.removeEventListener("keydown", this.handleKeydown, true);
    this.previouslyFocused?.focus();
  }

  /** Resolve `openPlanPicker()` with the chosen plan and close the overlay. */
  private resolvePlan(choice: PlanChoice): void {
    this.hideOverlay();
    const resolve = this.planResolve;
    this.planResolve = null;
    resolve?.(choice);
  }

  private close(): void {
    this.hideOverlay();
    if (this.pickerMode === "signup") {
      const resolve = this.planResolve;
      this.planResolve = null;
      resolve?.(null);
      return;
    }
    const resolve = this.resolve;
    this.resolve = null;
    resolve?.(this.redeemedSession);
  }
}

/** Why the paywall showed up, in the session's own terms. */
function describeReason(session: Session | null): string {
  const plan = session?.plan;
  if ((plan === "day" || plan === "pro") && session?.planExpiresAt != null) {
    return `Your ${planLabel(plan)} plan ${formatExpiry(session.planExpiresAt).toLowerCase()}.`;
  }
  return "You've used your free nest. Choose a plan to keep splitting.";
}
